import type { Navigate } from '../App'
import { useActiveIslands, useDrillableIslands, useStore } from '../state/store'
import { languageOf } from '../lib/languages'
import { dueIslands, practiceStreak, readiness, scoreClass, toneProfile } from '../lib/analysis'
import { dueLabel } from '../lib/srs'

const TONE_NAME: Record<number, string> = {
  1: 'first — high level',
  2: 'second — rising',
  3: 'third — dipping',
  4: 'fourth — falling',
  0: 'neutral',
}

export function Dashboard({ navigate }: { navigate: Navigate }) {
  const { data } = useStore()
  const islands = useActiveIslands()
  /*
   * Today is an assignment, not a catalogue. An island above the learner's
   * level is still theirs to open from the Islands screen — it is just not
   * something this page tells them to go and clear.
   */
  const drillable = useDrillableIslands()
  const ui = languageOf(data.settings.activeLanguage).ui
  const due = dueIslands(drillable)
  const streak = practiceStreak(data.history)
  const tones = toneProfile(data.history)
  const automatic = drillable.filter((i) => readiness(i) === 'automatic').length

  const weakestTone = tones.length
    ? tones.reduce((worst, row) => (row.accuracy < worst.accuracy ? row : worst))
    : null

  return (
    <>
      <header className="page-head">
        <div className="eyebrow">Today</div>
        <h1 className="page-title">
          <span className="page-title-zh">{ui.todayTitle}</span>
        </h1>
        <p className="lede">
          {due.length === 0
            ? 'Nothing is due. This is the day to build a new island, or take an ambush run for the sake of it.'
            : `${due.length} ${due.length === 1 ? 'island is' : 'islands are'} due. Clear those first — a lapsed island costs more to rebuild than to maintain.`}
        </p>
      </header>

      {!data.sync.token && (
        <div className="reveal" style={{ marginBottom: '1.4rem' }}>
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <div>
              <strong>Your islands live only on this device.</strong>
              <div className="muted" style={{ fontSize: '0.86rem' }}>
                Sign in to keep them on your phone and laptop together.
              </div>
            </div>
            <button
              type="button"
              className="btn is-primary"
              onClick={() => navigate({ view: 'signin' })}
            >
              Sign in
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-3" style={{ marginBottom: '1.4rem' }}>
        <div className="card">
          <div className="stat-value">{due.length}</div>
          <div className="stat-label">due now</div>
        </div>
        <div className="card">
          <div className="stat-value">
            {automatic}
            <span className="muted" style={{ fontSize: '0.9rem' }}>
              /{drillable.length}
            </span>
          </div>
          <div className="stat-label">automatic</div>
        </div>
        <div className="card">
          <div className="stat-value">{streak}</div>
          <div className="stat-label">day streak</div>
        </div>
      </div>

      <div className="row" style={{ marginBottom: '1.4rem' }}>
        <button type="button" className="btn is-live" onClick={() => navigate({ view: 'ambush' })}>
          Ambush me
        </button>
        <button type="button" className="btn" onClick={() => navigate({ view: 'scenarios' })}>
          Run a scenario
        </button>
      </div>

      {due.length > 0 && (
        <>
          <div className="stat-label" style={{ marginBottom: '0.5rem' }}>
            Due
          </div>
          <div className="stack" style={{ marginBottom: '1.6rem' }}>
            {due.map((island) => (
              <div key={island.id} className="card">
                <div className="card-head" style={{ marginBottom: 0 }}>
                  <div>
                    <div className="hanzi" style={{ fontSize: '1.15rem' }}>
                      {island.titleZh}
                    </div>
                    <div className="muted" style={{ fontSize: '0.82rem' }}>
                      {island.title} · {dueLabel(island.srs)}
                    </div>
                  </div>
                  <div className="row">
                    <span className={`badge is-${readiness(island)}`}>{readiness(island)}</span>
                    <button
                      type="button"
                      className="btn is-small is-primary"
                      onClick={() => navigate({ view: 'recall', id: island.id })}
                    >
                      Drill
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {tones.length > 0 && (
        <>
          <div className="stat-label" style={{ marginBottom: '0.5rem' }}>
            Tone profile
          </div>
          <div className="card" style={{ marginBottom: '1.6rem' }}>
            <p className="english" style={{ marginTop: 0 }}>
              {weakestTone && weakestTone.accuracy < 90 ? (
                <>
                  Across every pinyin drill you have run, your{' '}
                  <strong>{TONE_NAME[weakestTone.tone]}</strong> tone is the one going missing —{' '}
                  {weakestTone.accuracy}% right over {weakestTone.total} syllables.
                </>
              ) : (
                'Your tones are holding up across the board.'
              )}
            </p>

            <div className="stack" style={{ gap: '0.55rem' }}>
              {tones.map((row) => (
                <div key={row.tone}>
                  <div
                    className="row"
                    style={{ justifyContent: 'space-between', marginBottom: '0.2rem' }}
                  >
                    <span style={{ fontSize: '0.86rem' }}>{TONE_NAME[row.tone]}</span>
                    <span className="data muted" style={{ fontSize: '0.78rem' }}>
                      {row.accuracy}% · {row.right}/{row.total}
                    </span>
                  </div>
                  <div className="bar">
                    <div
                      className={`bar-fill ${
                        row.accuracy < 70 ? 'is-low' : row.accuracy < 88 ? 'is-mid' : ''
                      }`}
                      style={{ width: `${row.accuracy}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {data.history.length > 0 && (
        <>
          <div className="stat-label" style={{ marginBottom: '0.5rem' }}>
            Recent
          </div>
          <div className="card">
            {data.history.slice(0, 8).map((entry) => {
              const island = islands.find((i) => i.id === entry.islandId)
              return (
                <div key={entry.id} className="block-row" style={{ alignItems: 'center' }}>
                  <span className="badge">{entry.mode}</span>
                  <span className="hanzi" style={{ flex: 1, fontSize: '0.98rem' }}>
                    {island?.titleZh ?? '—'}
                  </span>
                  {entry.mode !== 'listen' && (
                    <span className={`data ${scoreClass(entry.score)}`}>{entry.score}</span>
                  )}
                  <span className="data muted" style={{ fontSize: '0.76rem', minWidth: '5.5rem', textAlign: 'right' }}>
                    {new Date(entry.at).toLocaleDateString()}
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}

      {data.history.length === 0 && (
        <div className="empty">
          No practice logged yet. Open an island and listen through it once, then try to produce it
          from the English alone.
        </div>
      )}
    </>
  )
}
